import React, { useEffect, useRef, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	MenuItem,
	Box,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { isBsuidNumber } from "../../helpers/formatContactIdentifier";

const useStyles = makeStyles((theme) => ({
	helper: {
		display: "block",
		marginTop: 4,
		color: theme.palette.text.secondary,
	},
	error: {
		marginTop: 12,
		padding: 12,
		borderRadius: 8,
		background: "#fef2f2",
		color: "#991b1b",
		fontSize: 13,
	},
	settingsLink: {
		marginInlineStart: 6,
		color: "#075e54",
		textDecoration: "underline",
		cursor: "pointer",
	},
	contact: {
		fontSize: 13,
		color: theme.palette.text.secondary,
		marginBottom: 12,
	},
	// Amount + currency: side-by-side on desktop, stacked on phones so the
	// fixed-width currency select doesn't squeeze the amount field.
	amountRow: {
		display: "flex",
		gap: 8,
		[theme.breakpoints.down("xs")]: { flexDirection: "column" },
	},
	currencySelect: {
		minWidth: 110,
		[theme.breakpoints.down("xs")]: { minWidth: "100%" },
	},
}));

const T = (key, fallback) =>
	i18n.t(`requestPaymentDialog.${key}`) || fallback;

// Tranzila bills in 2-decimal-place currency (1 cent / 1 agorah). Anything
// the agent types beyond two decimals would either get truncated by
// Tranzila or, worse, charged at a different value than what the dialog
// previewed. We round here so the preview and the charge agree, and so
// `1e308` / scientific noise stops parser-level shenanigans early.
const MAX_AMOUNT = 100000;
const sanitizeAmount = (v) => {
	const n = Number(v);
	if (!Number.isFinite(n)) return null;
	const rounded = Math.round(n * 100) / 100;
	if (rounded < 0.01 || rounded > MAX_AMOUNT) return null;
	return rounded;
};

/**
 * Lets an agent generate a Tranzila payment link for the customer in this
 * ticket and dispatch it as a WhatsApp message in one click.
 *
 * Manual-only by design: AI never reaches this flow. Amount comes from
 * the agent's own input (or pre-fill from a verified external CRM
 * lookup), never from free conversation context.
 */
const RequestPaymentDialog = ({ open, onClose, ticket }) => {
	const classes = useStyles();
	const history = useHistory();

	const [amount, setAmount] = useState("");
	const [currency, setCurrency] = useState("ILS");
	const [description, setDescription] = useState("");
	const [sending, setSending] = useState(false);
	const [error, setError] = useState(null);
	const [confirmStep, setConfirmStep] = useState(false);
	// Defends the Send button against double-click + rapid Enter taps that
	// would otherwise fire two POST /payment-links — both succeed, customer
	// receives two WhatsApp messages with two valid links and can pay either.
	// `setSending(true)` happens inside React's batched render, so a fast
	// second click slips through before the disabled prop applies; this ref
	// flips synchronously and is checked at the very top of handleSend.
	const inFlightRef = useRef(false);
	// Tranzila accounts the agent's company has configured. We load them
	// up-front so:
	//   - 0 active → show "configure first" inline error and bail early
	//   - 1 active → use it implicitly (no selector clutter)
	//   - 2+ active → render a dropdown so the agent picks which terminal
	//     receives this customer's payment
	const [accounts, setAccounts] = useState([]);
	const [accountId, setAccountId] = useState("");
	const [accountsLoading, setAccountsLoading] = useState(false);

	// Pre-fill from ticket.notes if a previous external CRM lookup left
	// structured `amount_due` / `description` lines in there. This is a
	// best-effort regex parse — if the notes are free text we just leave
	// the form empty and the agent fills it manually.
	useEffect(() => {
		if (!open) return;
		setError(null);
		setConfirmStep(false);
		setSending(false);
		const notes = String(ticket?.notes || "");
		const amountMatch = notes.match(/amount_due\s*[:=]\s*([0-9]+(?:\.[0-9]+)?)/i);
		const descMatch = notes.match(/description\s*[:=]\s*(.+?)(?:\n|$)/i);
		setAmount(amountMatch ? amountMatch[1] : "");
		setDescription(descMatch ? descMatch[1].trim().slice(0, 80) : "");
		setCurrency("ILS");

		// Pull tenant's Tranzila accounts so the agent can pick which
		// terminal receives this payment when more than one is configured.
		let cancelled = false;
		setAccountsLoading(true);
		api
			.get("/tranzila-accounts")
			.then((res) => {
				if (cancelled) return;
				const list = Array.isArray(res?.data) ? res.data : [];
				const active = list.filter((a) => a.isActive !== false);
				setAccounts(active);
				const preferred =
					active.find((a) => a.isDefault) || active[0] || null;
				setAccountId(preferred ? String(preferred.id) : "");
				if (preferred?.defaultCurrency) {
					setCurrency(String(preferred.defaultCurrency).toUpperCase());
				}
			})
			.catch(() => {
				if (cancelled) return;
				// Fall through silently — submit will surface the real backend
				// error (NO_TERMINAL etc.) if the user actually tries to send.
				setAccounts([]);
			})
			.finally(() => {
				if (!cancelled) setAccountsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, ticket]);

	const contactName = ticket?.contact?.name || T("unnamedContact", "this contact");
	// A BSUID (username-only) contact stores a synthetic wau_ key in `number`;
	// never show it as a phone. Omit the parenthetical for such contacts.
	const contactPhone = isBsuidNumber(ticket?.contact?.number)
		? ""
		: ticket?.contact?.number || "";

	const numericAmount = sanitizeAmount(amount);
	const amountValid = numericAmount !== null;
	const descriptionValid = description.trim().length > 0 && description.trim().length <= 80;

	const handleNext = () => {
		setError(null);
		if (!amountValid) {
			setError(
				T(
					"invalidAmount",
					`Enter an amount between 0.01 and ${MAX_AMOUNT} (max 2 decimals).`,
				),
			);
			return;
		}
		if (!descriptionValid) {
			setError(T("invalidDescription", "Enter a short description (max 80 chars)."));
			return;
		}
		setConfirmStep(true);
	};

	const handleSend = async () => {
		// Synchronous guard against double-clicks. setSending() below would
		// disable the button on the next render, but a fast second click can
		// already be queued before that paint lands.
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setSending(true);
		setError(null);
		try {
			const payload = {
				ticketId: ticket.id,
				amount: numericAmount,
				currency,
				description: description.trim(),
				sendToChat: true,
			};
			// Pass accountId only when the agent had more than one terminal
			// to choose between. With 0 or 1 accounts we let the backend
			// default lookup do its thing.
			if (accountId && accounts.length > 1) {
				payload.accountId = Number(accountId);
			}
			const { data } = await api.post("/payment-links", payload);
			// Backend returns chatDelivery:{ok:false,...} when the link was
			// generated but the WhatsApp message couldn't be delivered (for
			// example: Cloud API session expired, Baileys disconnected).
			// Surface that as a warning instead of a misleading "Sent!" toast.
			if (data?.chatDelivery && data.chatDelivery.ok === false) {
				toast.warn(
					T("partialSuccess", "Link generated, but chat delivery failed: ") +
						(data.chatDelivery.error || "unknown error"),
				);
			} else {
				toast.success(T("sentSuccess", "Payment link sent to the customer."));
			}
			onClose();
		} catch (err) {
			const code = err?.response?.data?.error;
			// Backend signals "no Tranzila account" with this exact code; we
			// turn it into an actionable message + link to Settings instead
			// of dumping the raw enum.
			if (code === "ERR_NO_TRANZILA_ACCOUNT_CONFIGURED") {
				setError("__NO_TERMINAL__");
			} else {
				setError(
					code ||
						err?.message ||
						T("sendFailed", "Failed to send payment link. Please try again."),
				);
			}
		} finally {
			setSending(false);
			inFlightRef.current = false;
		}
	};

	const handleClose = () => {
		if (sending) return;
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle>
				{confirmStep
					? T("confirmTitle", "Confirm payment request")
					: T("title", "Request payment")}
			</DialogTitle>
			<DialogContent>
				<Typography className={classes.contact}>
					{T("recipient", "Recipient")}:{" "}
					<strong>{contactName}</strong>
					{contactPhone && ` (${contactPhone})`}
				</Typography>

				{!confirmStep && (
					<>
						{accounts.length > 1 && (
							<TextField
								select
								label={T("terminalLabel", "Terminal")}
								value={accountId}
								onChange={(e) => setAccountId(e.target.value)}
								variant="outlined"
								margin="dense"
								fullWidth
								helperText={T(
									"terminalHelp",
									"Money will land on this terminal's merchant account.",
								)}
							>
								{accounts.map((a) => (
									<MenuItem key={a.id} value={String(a.id)}>
										{a.label || a.terminalName}
										{a.terminalName && a.label && ` (${a.terminalName})`}
										{a.isDefault ? " ⭐" : ""}
									</MenuItem>
								))}
							</TextField>
						)}
						<Box className={classes.amountRow}>
							<TextField
								label={T("amountLabel", "Amount")}
								type="number"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								variant="outlined"
								margin="dense"
								fullWidth
								inputProps={{ min: 0.01, step: 0.01, inputMode: "decimal" }}
								required
								autoFocus
							/>
							<TextField
								select
								label={T("currencyLabel", "Currency")}
								value={currency}
								onChange={(e) => setCurrency(e.target.value)}
								variant="outlined"
								margin="dense"
								className={classes.currencySelect}
							>
								<MenuItem value="ILS">ILS ₪</MenuItem>
								<MenuItem value="USD">USD $</MenuItem>
								<MenuItem value="EUR">EUR €</MenuItem>
								<MenuItem value="GBP">GBP £</MenuItem>
							</TextField>
						</Box>
						<TextField
							label={T("descriptionLabel", "Description")}
							value={description}
							onChange={(e) => setDescription(e.target.value.slice(0, 80))}
							variant="outlined"
							margin="dense"
							fullWidth
							required
							helperText={
								<span className={classes.helper}>
									{T(
										"descriptionHelp",
										"Shown to the customer on the Tranzila page. Max 80 chars.",
									)}
									{` (${description.length}/80)`}
								</span>
							}
						/>
					</>
				)}

				{confirmStep && (
					<Box mt={1}>
						<Typography variant="body1" gutterBottom>
							{T("confirmBody", "Send a payment link for")}{" "}
							<strong>
								{numericAmount?.toFixed(2)} {currency}
							</strong>{" "}
							{T("confirmTo", "to")} <strong>{contactName}</strong>?
						</Typography>
						<Typography variant="caption" color="textSecondary">
							{T(
								"confirmHelp",
								"The link will be delivered as a WhatsApp message in this conversation.",
							)}
						</Typography>
					</Box>
				)}

				{error && (
					<div className={classes.error}>
						{error === "__NO_TERMINAL__" ? (
							<>
								{T(
									"noTerminal",
									"No Tranzila terminal configured for your company.",
								)}
								<span
									className={classes.settingsLink}
									onClick={() => {
										onClose();
										history.push("/settings");
									}}
								>
									{T("openSettings", "Open Settings → Payments")}
								</span>
							</>
						) : (
							error
						)}
					</div>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={sending} color="default">
					{T("cancel", "Cancel")}
				</Button>
				{!confirmStep && (
					<Button
						onClick={handleNext}
						color="primary"
						variant="contained"
						disabled={!amountValid || !descriptionValid}
					>
						{T("next", "Next")}
					</Button>
				)}
				{confirmStep && (
					<Button
						onClick={handleSend}
						color="primary"
						variant="contained"
						disabled={sending}
					>
						{sending ? (
							<CircularProgress size={16} style={{ color: "#fff" }} />
						) : (
							T("sendNow", "Send payment link")
						)}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default RequestPaymentDialog;
